import * as React from 'react';
import {
    AutoSizer,
    CellMeasurer,
    CellMeasurerCache,
    Masonry,
    Size,
    Positioner,
    MasonryCellProps,
    createMasonryCellPositioner
} from 'react-virtualized';
import Modal from '@mui/material/Modal';
import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import FontAwesomeIcon from 'react-fontawesome';
import { FinalizeOrderType, Order, Store } from './types';
import { getProductName, getTableCode } from './helpers';
import * as Api from './Api';

const OrderComponent: React.FC<{ order: Order }> = ({ order }) => {
    const [modalOpen, setModalOpen] = React.useState(false);

    const tables = useSelector(({ app }: Store) => app.tables);
    const products = useSelector(({ app }: Store) => app.products);

    const orderHasComments = React.useCallback(
        () => order.orderItems.reduce((acc, item) => !!item.comments || acc, false),
        [order?.orderItems]
    );

    const handleDialogClose = React.useCallback(() => {
        setModalOpen(false);
    }, []);

    const handleCompleteOrder = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();

            handleDialogClose();
            Api.finalizeOrder(order.id, FinalizeOrderType.PrepareOrder);
        },
        [handleDialogClose, order?.id]
    );

    const renderActions = React.useCallback(
        (modal: boolean = false) => (
            <div className='actions'>
                {!modal && orderHasComments() && (
                    <>
                        <FontAwesomeIcon name='commenting' className='has-comments' />
                        <button className='show-order' type='button'>
                            SHOW
                        </button>
                    </>
                )}
                {(modal || !orderHasComments()) && (
                    <button
                        className='complete-order'
                        type='button'
                        onClick={handleCompleteOrder}>
                        COMPLETE
                    </button>
                )}
            </div>
        ),
        [handleCompleteOrder, orderHasComments]
    );

    const handleCloseClick = React.useCallback(
        (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
            e.stopPropagation();
            handleDialogClose();
        },
        [handleDialogClose]
    );

    const renderOrderTitle = React.useCallback(
        (modal: boolean = false) => (
            <div className='table-id'>
                <div>Table {getTableCode(tables, order.tableID)}</div>
                {modal && (
                    <FontAwesomeIcon
                        name='times-circle'
                        className='close-icon'
                        onClick={handleCloseClick}
                    />
                )}
            </div>
        ),
        [handleCloseClick, order?.tableID, tables]
    );

    const renderOrderItems = React.useCallback(
        (modal: boolean = false) => (
            <div className='order-items'>
                {order.orderItems.map(
                    ({ id, quantity, productID, comments, productVarietyID }) => {
                        const product = products.find(p => p.id === productID);
                        const variety = product?.productVarieties?.find(
                            v => v.id === productVarietyID
                        );

                        return (
                            <div className='product' key={id}>
                                <div className='left'>
                                    <div className='name'>
                                        {getProductName(products, productID)}
                                    </div>
                                    <div className='variety'>{variety?.name}</div>
                                    {comments && modal && (
                                        <div className='comments'>{comments}</div>
                                    )}
                                </div>
                                <div className='quantity'>x{quantity}</div>
                            </div>
                        );
                    }
                )}
            </div>
        ),
        [order?.orderItems, products]
    );

    const handleOrderClick = React.useCallback(() => {
        setModalOpen(true);
    }, []);

    if (!order) {
        return null;
    }

    return (
        <>
            <div className='order container' onClick={handleOrderClick}>
                {renderOrderTitle()}
                {renderOrderItems()}
                {renderActions()}
            </div>
            <Modal
                open={modalOpen}
                onClose={handleDialogClose}
                BackdropProps={{ classes: { root: 'backdrop' } }}
                classes={{ root: 'modal-dialog' }}>
                <div className='order container modal'>
                    {renderOrderTitle(true)}
                    {renderOrderItems(true)}
                    {renderActions(true)}
                </div>
            </Modal>
        </>
    );
};

const COLUMN_WIDTH = 300;
const GUTTER_SIZE = 20;

const Orders: React.FC<RouteComponentProps> = () => {
    const openOrders = useSelector(({ app }: Store) => app.openOrders);

    const widthRef = React.useRef<number>();
    const columnCount = React.useRef<number>();
    const masonry = React.useRef<Masonry>();
    const cache = React.useRef(
        new CellMeasurerCache({
            defaultHeight: 250,
            defaultWidth: 200,
            fixedWidth: true
        })
    );
    const cellPositioner = React.useRef<Positioner>(
        createMasonryCellPositioner({
            cellMeasurerCache: cache.current,
            columnCount: columnCount.current,
            columnWidth: COLUMN_WIDTH,
            spacer: GUTTER_SIZE
        })
    );

    const resetCellPositioner = React.useCallback(() => {
        cellPositioner.current.reset({
            columnCount: columnCount.current,
            columnWidth: COLUMN_WIDTH,
            spacer: GUTTER_SIZE
        });
    }, []);

    React.useEffect(() => {
        cache.current.clearAll();
        resetCellPositioner();

        masonry.current.clearCellPositions();
        masonry.current.recomputeCellPositions();
    }, [openOrders, resetCellPositioner]);

    const cellRenderer = React.useCallback(
        ({ index, key, parent, style }: MasonryCellProps) => (
            <CellMeasurer cache={cache.current} index={index} key={key} parent={parent}>
                <div style={{ ...style, width: COLUMN_WIDTH }}>
                    <OrderComponent order={openOrders[index]} />
                </div>
            </CellMeasurer>
        ),
        [openOrders]
    );

    const calculateColumnCount = React.useCallback(() => {
        columnCount.current = Math.floor(
            widthRef.current / (COLUMN_WIDTH + GUTTER_SIZE)
        );
    }, []);

    const handleResize = React.useCallback(
        ({ width }: Size) => {
            widthRef.current = width;
            calculateColumnCount();
            resetCellPositioner();
            masonry.current.recomputeCellPositions();
        },
        [calculateColumnCount, resetCellPositioner]
    );

    return (
        <div className='open-orders'>
            <AutoSizer onResize={handleResize}>
                {({ width, height }) => {
                    widthRef.current = width;

                    calculateColumnCount();

                    return (
                        <Masonry
                            cellCount={openOrders.length}
                            cellMeasurerCache={cache.current}
                            cellPositioner={cellPositioner.current}
                            cellRenderer={cellRenderer}
                            height={height}
                            width={widthRef.current}
                            autoHeight={false}
                            style={{ outline: 'none' }}
                            ref={masonry}
                        />
                    );
                }}
            </AutoSizer>
        </div>
    );
};

export default Orders;
